/* @flow */
import * as React from "react";
import PropTypes from "prop-types";
import { darken } from "polished";
import {
  withColorsProvider,
  type ColorSet,
} from "nx-components/ColorsProvider";
import NoStyleButton from "nx-components/NoStyleButton";
import Tappable, { ACTIVE_DARKEN_AMOUNT } from "nx-components/Tappable";
import Theme from "nx-components/Theme";
import prefixStyles from "nx-components/utils/prefixStyles";
import { IconDisclosureArrowRight } from "nx-components/Icons";
import Session from "nexia_mobile/models/session";
import connect from "nexia_mobile/flux_like/store/connect";
import { setValueAtPath } from "nexia_mobile/flux_like/store";
import BackboneListener from "nexia_mobile/components/BackboneListener";
import ChoiceDialog from "nexia_mobile/components/Dialogs/ChoiceDialog";

type Buttons = Array<{
  action: "href" | "dismiss",
  text: string,
  type?: ?string,
  href?: ?string,
}>;

type ChoiceItemProps = {
  open: boolean,
  title: string,
  text: string, // html
  buttons?: ?Buttons,
  persistenceText: string,
  openDialog: () => void,
  closeDialog: () => void,
  colors: ColorSet,
};

class ChoiceItemWithoutColor extends React.Component<ChoiceItemProps> {
  static contextTypes = {
    isLargeAppLayout: PropTypes.bool,
  };

  render() {
    const {
      open,
      title,
      text,
      buttons,
      persistenceText,
      openDialog,
      closeDialog,
      colors,
    } = this.props;

    const leftSafePadding = this.context.isLargeAppLayout
      ? "0px"
      : Theme.Padding.SAFE_AREA_LEFT;

    const styles = prefixStyles({
      choice: {
        color: colors.PRIMARY_TEXT,
        fontSize: "14px",
        padding: "0.5em",
        textAlign: "left",
      },
      flex: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      },
      icon: {
        fontSize: "16px",
        flex: "0 0 auto",
      },
    });

    return (
      <React.Fragment>
        <Tappable onTap={openDialog}>
          {({ active, tapTargetRefHandler }) => (
            <NoStyleButton
              style={{
                ...styles.choice,
                width: `calc(100% + ${leftSafePadding} + ${Theme.Padding.SAFE_AREA_RIGHT})`,
                marginLeft: `calc(-1 * ${leftSafePadding})`,
                backgroundColor: active
                  ? darken(ACTIVE_DARKEN_AMOUNT, colors.TEXT)
                  : colors.TEXT,
              }}
              innerRef={tapTargetRefHandler}
            >
              <div style={styles.flex}>
                <span>{persistenceText}</span>
                <IconDisclosureArrowRight style={styles.icon} />
              </div>
            </NoStyleButton>
          )}
        </Tappable>
        <ChoiceDialog
          open={open}
          title={title}
          text={text}
          buttons={buttons}
          onDismiss={closeDialog}
        />
      </React.Fragment>
    );
  }
}

const ChoiceItem = withColorsProvider(ChoiceItemWithoutColor);

type ChoicesBannerProps = {
  choices: Array<{
    id: string,
    title: string,
    text: string, // html
    buttons?: ?Buttons,
    persistenceText: string,
  }>,
  forceChoiceDialogOpen: boolean,
  onDismiss: () => void,
};

type ChoicesBannerState = {
  openId: ?string,
};

class ChoicesBanner extends React.Component<
  ChoicesBannerProps,
  ChoicesBannerState
> {
  state = {
    openId: null,
  };

  UNSAFE_componentWillMount() {
    const { choices } = this.props;
    if (this.props.forceChoiceDialogOpen && choices.length > 0) {
      this.setState({ openId: choices[0].id });
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    // Determine which, if any, dialog should be open
    const { choices } = this.props;
    let openId = this.state.openId;
    if (!choices.find((choice) => choice.id === openId)) {
      openId = null;
    }
    if (
      nextProps.forceChoiceDialogOpen &&
      choices.length > 0 &&
      openId == null
    ) {
      openId = choices[0].id;
    }
    this.setState({ openId });
  }

  _handleCloseDialog = () => {
    this.setState({ openId: null });
    this.props.onDismiss();
  };

  render() {
    const { choices } = this.props;
    const { openId } = this.state;

    return choices.map((choice) => (
      <ChoiceItem
        key={choice.id}
        {...choice}
        open={choice.id === openId}
        openDialog={() => this.setState({ openId: choice.id })}
        closeDialog={this._handleCloseDialog}
      />
    ));
  }
}

type ChoicesBannerContainerProps = {
  session: Session,
  forceChoiceDialogOpen: boolean,
};

class ChoicesBannerContainer extends React.Component<ChoicesBannerContainerProps> {
  static propTypes = {
    session: PropTypes.instanceOf(Session).isRequired,
    forceChoiceDialogOpen: PropTypes.bool.isRequired,
  };

  render() {
    const { session, forceChoiceDialogOpen } = this.props;

    if (!session) {
      return null;
    }

    return (
      <BackboneListener listenTo={session} events={["change:choices"]}>
        {() => {
          const choices = session.get("choices");

          if (!choices) {
            return null;
          }

          return (
            <BackboneListener
              listenTo={choices}
              events={["add", "remove", "reset", "sort", "sync", "change"]}
              debounceRender
            >
              {() => (
                <ChoicesBanner
                  choices={choices.map((choice) => ({
                    id: choice.get("id"),
                    title: choice.get("title"),
                    text: choice.get("text"),
                    buttons: choice
                      .get("buttons")
                      .map((button) => button.attributes),
                    persistenceText: choice.get("persistence_text"),
                  }))}
                  forceChoiceDialogOpen={forceChoiceDialogOpen}
                  onDismiss={() => {
                    setTimeout(() => {
                      // I dont' know why this timeout is needed, but without it
                      // the dialog doesn't close for about a second (maybe when
                      // the fetch completes?)
                      setValueAtPath("forceChoiceDialogOpen", false);
                    });
                    choices.fetch();
                  }}
                />
              )}
            </BackboneListener>
          );
        }}
      </BackboneListener>
    );
  }
}

export default connect(
  {
    session: "session",
    forceChoiceDialogOpen: "forceChoiceDialogOpen",
  },
  ChoicesBannerContainer
);



// WEBPACK FOOTER //
// ./src/components/TopBar/ChoicesBannerContainer.jsx